import { CoreError, assertSafeInt } from './errors'
import type { BuildContext, NumberingInput } from './documents'
import type { Paisa } from './money'
import { formatDocNumber } from './numbering'
import type { Milli } from './qty'
import { mulDivRound } from './rounding'
import type {
  PurchaseOrderLineRow,
  PurchaseOrderRow,
  PurchaseOrderStatus,
  SyncOp,
  SyncRowBase,
} from './rows'

declare const crypto: { randomUUID(): string }

// Purchase orders. Like a sales order, a purchase order is paperwork: nothing
// here writes a stock movement. Stock arrives on a goods receipt whose lines
// point at the order lines they fill, and what has arrived is summed from
// those lines, never stored.

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

// approved and rejected are set by the server's decide_document, never by a device.
// issued is numbered and ready to send (po-ready.md); draft -> sent stays for older devices.
const MOVES: Partial<Record<PurchaseOrderStatus, PurchaseOrderStatus[]>> = {
  draft: ['issued', 'sent', 'submitted'],
  issued: ['sent', 'closed', 'cancelled'],
  submitted: ['cancelled'],
  approved: ['sent', 'cancelled'],
  sent: ['closed', 'cancelled'],
}

/** The status moves the server allows. */
export function canMovePurchaseOrder(
  order: Pick<PurchaseOrderRow, 'status'>,
  to: PurchaseOrderStatus,
): boolean {
  return MOVES[order.status]?.includes(to) ?? false
}

export function numberFields(numbering: NumberingInput | undefined, numbered: boolean) {
  if (!numbered)
    return { counter_id: numbering?.counter.id ?? null, fiscal_year: null, seq: null, number: null }
  if (!numbering) throw new CoreError('numbering_required', 'a sent order needs a counter')
  const { counter, prefix, fiscalYear, seq } = numbering
  if (counter.kind === 'web') {
    return { counter_id: counter.id, fiscal_year: fiscalYear, seq: null, number: null }
  }
  if (seq === undefined)
    throw new CoreError('numbering_required', 'device counters need the next seq')
  return {
    counter_id: counter.id,
    fiscal_year: fiscalYear,
    seq,
    number: formatDocNumber(prefix, counter.code, fiscalYear, seq),
  }
}

export interface PurchaseLineInput {
  productId: string
  qtyMilli: Milli
  unitCostPaisa: Paisa
  /** The purchase request line it was made from. */
  requestLineId?: string | null
}

/** What a line costs: round(qty × unit cost), as the server adds the order up. */
export function purchaseLineCost(
  line: Pick<PurchaseLineInput, 'qtyMilli' | 'unitCostPaisa'>,
): Paisa {
  return mulDivRound(line.qtyMilli, line.unitCostPaisa, 1000)
}

export interface BuildPurchaseOrderInput {
  id?: string
  /**
   * `issued` (generated, ready to send) or `submitted` when an approval step applies to the
   * total; both are numbered like sent.
   */
  status: 'draft' | 'issued' | 'sent' | 'submitted'
  supplierId: string | null
  /** Where the goods are expected. */
  locationId?: string | null
  orderDate: string
  expectedDate?: string | null
  notes?: string | null
  lines: readonly PurchaseLineInput[]
  /** Needed for a sent order. */
  numbering?: NumberingInput
}

export interface BuiltPurchaseOrder {
  order: PurchaseOrderRow
  lines: PurchaseOrderLineRow[]
  ops: SyncOp[]
}

/**
 * A purchase order with its lines, as one tx. Saving a draft again with the
 * same `id` replaces it: pass the stored lines as `replaces` so the ones that
 * are gone are deleted in the same tx.
 */
export function buildPurchaseOrder(
  ctx: BuildContext,
  input: BuildPurchaseOrderInput,
  replaces: readonly Pick<PurchaseOrderLineRow, 'id'>[] = [],
): BuiltPurchaseOrder {
  if (input.lines.length === 0)
    throw new CoreError('invalid_order', 'a purchase order needs at least one line')
  if (input.status !== 'draft' && !input.supplierId)
    throw new CoreError('invalid_order', 'choose the supplier the order goes to')
  for (const l of input.lines) {
    assertSafeInt(l.qtyMilli, 'qtyMilli')
    assertSafeInt(l.unitCostPaisa, 'unitCostPaisa')
    if (l.qtyMilli <= 0) throw new CoreError('invalid_line', 'quantity must be positive')
    if (l.unitCostPaisa < 0) throw new CoreError('invalid_line', 'cost cannot be negative')
  }
  const order: PurchaseOrderRow = {
    ...base(ctx, input.id),
    supplier_id: input.supplierId,
    location_id: input.locationId ?? null,
    status: input.status,
    ...numberFields(input.numbering, input.status !== 'draft'),
    closed_at: null,
    close_reason: null,
    order_date: input.orderDate,
    expected_date: input.expectedDate ?? null,
    notes: input.notes?.trim() || null,
    total_paisa: input.lines.reduce((sum, l) => sum + purchaseLineCost(l), 0),
  }
  const lines: PurchaseOrderLineRow[] = input.lines.map((l, i) => ({
    ...base(ctx),
    purchase_order_id: order.id,
    line_no: i + 1,
    product_id: l.productId,
    qty_ordered_milli: l.qtyMilli,
    unit_cost_paisa: l.unitCostPaisa,
    purchase_request_line_id: l.requestLineId ?? null,
  }))
  return {
    order,
    lines,
    ops: [
      op('purchase_orders', order),
      ...replaces.map((r): SyncOp => ({
        table: 'purchase_order_lines',
        op: 'delete',
        row: { id: r.id },
      })),
      ...lines.map((r) => op('purchase_order_lines', r)),
    ],
  }
}

/**
 * Sends or submits a draft (pass `numbering`), sends an approved order, or closes / cancels
 * one with a reason.
 */
export function setPurchaseOrderStatus(
  ctx: BuildContext,
  order: PurchaseOrderRow,
  to: PurchaseOrderStatus,
  options: { reason?: string; numbering?: NumberingInput } = {},
): { order: PurchaseOrderRow; ops: SyncOp[] } {
  if (!canMovePurchaseOrder(order, to))
    throw new CoreError(
      'invalid_transition',
      `a ${order.status} purchase order cannot become ${to}`,
    )
  if ((to === 'issued' || to === 'sent' || to === 'submitted') && !order.supplier_id)
    throw new CoreError('invalid_order', 'choose the supplier the order goes to')
  const closing = to === 'closed' || to === 'cancelled'
  const reason = options.reason?.trim() ?? ''
  if (closing && !reason) throw new CoreError('invalid_input', 'a reason is required')
  const next: PurchaseOrderRow = {
    ...order,
    status: to,
    ...(order.status === 'draft' ? numberFields(options.numbering, true) : {}),
    closed_at: closing ? ctx.now : null,
    close_reason: closing ? reason : null,
    updated_at: ctx.now,
  }
  return { order: next, ops: [op('purchase_orders', next)] }
}

/** Quantity already on goods receipts, by order line id. */
export type ReceivedByLine = Readonly<Record<string, Milli>>

export type PurchaseProgress = 'none' | 'part' | 'full' | 'over'

/** How much of an order has arrived: summed from the receipts, never stored. */
export function purchaseProgress(
  lines: readonly Pick<PurchaseOrderLineRow, 'id' | 'qty_ordered_milli'>[],
  received: ReceivedByLine,
): { orderedMilli: Milli; receivedMilli: Milli; progress: PurchaseProgress } {
  let orderedMilli = 0
  let receivedMilli = 0
  let over = false
  let short = false
  for (const l of lines) {
    const done = received[l.id] ?? 0
    orderedMilli += l.qty_ordered_milli
    receivedMilli += done
    if (done > l.qty_ordered_milli) over = true
    if (done < l.qty_ordered_milli) short = true
  }
  const progress: PurchaseProgress =
    receivedMilli === 0 ? 'none' : short ? 'part' : over ? 'over' : 'full'
  return { orderedMilli, receivedMilli, progress }
}

/** What is still to arrive on a line (never below zero). */
export function purchaseRemaining(
  line: Pick<PurchaseOrderLineRow, 'id' | 'qty_ordered_milli'>,
  received: ReceivedByLine,
): Milli {
  return Math.max(0, line.qty_ordered_milli - (received[line.id] ?? 0))
}
